import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Loader2, Check, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { qualityNcrAPI } from '@/lib/api';
import { StatusBadge, DeadlineBadge, fmtDate } from './ncr/NcrShared';
import { ClassifyButton, ExportRange } from './ncr/RegisterTools';
import { NewComplaintDialog } from './ncr/NewComplaintDialog';
import { LegacyImportButton } from './ncr/NcrExtras';
import { MapCustomerDialog } from './ncr/MapCustomerDialog';
import { useAuth } from '@/contexts/AuthContext';

const STATUSES = ['', 'Open', 'Resolved', 'Closed'];

export default function CustomerComplaintsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [customer, setCustomer] = useState(() => new URLSearchParams(window.location.search).get('customer') || '');
  const [overdue, setOverdue] = useState(false);
  const [mapRow, setMapRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await qualityNcrAPI.listComplaints({ status: status || undefined, q: q || undefined, overdue, customer: customer || undefined })).data); }
    catch { toast.error('Failed to load complaints'); } finally { setLoading(false); }
  }, [status, q, overdue, customer]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="customer-complaints-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Customer Complaints</h1>
          <p className="text-xs text-muted-foreground">External rejections · correction &amp; containment to customer in 7 days · corrective action in 1 month · auto-closes 3 months after with no repeat</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <LegacyImportButton kind="complaint" onDone={load} />}
          <ClassifyButton kind="complaint" onChanged={load} />
          <ExportRange label="Download F/QA/14" exporter={qualityNcrAPI.exportComplaints} fallback="F-QA-14.xlsx" testid="complaints-export" />
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => setOpen(true)} data-testid="complaints-new-btn"><Plus className="w-4 h-4 mr-1" />New complaint</Button>
        </div>
      </div>
      <Card className="shadow-none"><CardContent className="p-3 flex flex-wrap items-center gap-3">
        <div className="flex rounded-md border overflow-hidden" data-testid="complaints-status-tabs">
          {STATUSES.map((s) => (
            <button key={s || 'all'} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-xs ${status === s ? 'bg-teal-600 text-white' : 'hover:bg-muted'}`} data-testid={`complaints-status-${s || 'all'}`}>{s || 'All'}</button>
          ))}
        </div>
        <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search no., customer, part, DC" className="pl-8 h-9 w-72" data-testid="complaints-search-input" /></div>
        <div className="flex items-center gap-2"><Switch checked={overdue} onCheckedChange={setOverdue} data-testid="complaints-overdue-switch" /><Label className="text-xs">Overdue only</Label></div>
        {customer && <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs px-2.5 py-1" data-testid="complaints-customer-chip">
          Customer: {customer.replace(/\n/g, ' ')}<button onClick={() => setCustomer('')} data-testid="complaints-customer-chip-clear"><X className="w-3 h-3" /></button></span>}
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </CardContent></Card>
      <Card className="shadow-none"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs" data-testid="complaints-table">
          <thead className="bg-muted/50 text-muted-foreground"><tr>
            {['No.', 'Reported', 'Customer', 'Part', 'Qty', 'Reason', 'R/N', 'DC', 'Containment (7d)', 'Corrective (1m)', '8D', 'Status'].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={12} className="text-center py-6 text-muted-foreground">No complaints</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} className="border-t hover:bg-muted/40 cursor-pointer" onClick={() => navigate(`/quality/complaints/${c.id}`)} data-testid={`complaint-row-${c.id}`}>
                <td className="px-3 py-2 font-mono text-blue-700">{c.number}</td>
                <td className="px-3 py-2">{fmtDate(c.reported_date)}</td>
                <td className="px-3 py-2"><span className="whitespace-pre-line">{c.customer_name}</span>
                  {isAdmin && c.legacy && <button className="ml-1 align-middle text-muted-foreground hover:text-teal-700" title="Map to system customer"
                    onClick={(e) => { e.stopPropagation(); setMapRow(c); }} data-testid={`complaint-customer-edit-${c.id}`}><Pencil className="w-3 h-3 inline" /></button>}</td>
                <td className="px-3 py-2"><span className="font-mono">{c.part_no}</span> <span className="text-muted-foreground">{c.part_name}</span></td>
                <td className="px-3 py-2 font-mono">{c.quantity}</td>
                <td className="px-3 py-2">{c.reason}</td>
                <td className={`px-3 py-2 font-semibold ${c.repetitive === 'R' ? 'text-red-600' : ''}`}>{c.repetitive}</td>
                <td className="px-3 py-2 font-mono">{c.dc_number}</td>
                <td className="px-3 py-2"><DeadlineBadge state={c.containment_state} due={c.containment_due} testid={`complaint-containment-${c.id}`} /></td>
                <td className="px-3 py-2"><DeadlineBadge state={c.corrective_state} due={c.corrective_due} testid={`complaint-corrective-${c.id}`} /></td>
                <td className="px-3 py-2">{c.eight_d_complete || c.has_8d_file ? <Check className="w-4 h-4 text-emerald-600" /> : '—'}</td>
                <td className="px-3 py-2"><StatusBadge status={c.status} testid={`complaint-status-${c.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
      <NewComplaintDialog open={open} onClose={() => setOpen(false)} onCreated={(c) => { setOpen(false); navigate(`/quality/complaints/${c.id}`); }} />
      {mapRow && <MapCustomerDialog complaint={mapRow} open={!!mapRow} onClose={() => setMapRow(null)} onMapped={() => { setMapRow(null); load(); }} />}
    </div>
  );
}
